/**
 * Secure customer files — compliance documents stored *in Salesforce*, not S3.
 *
 * Some customer documents must live in the Salesforce org of record rather than the toolbox's
 * object storage, for compliance. The bytes are stored as a Salesforce **File** (a
 * `ContentVersion` under a `ContentDocument`, linked to the Account with `ShareType='V'` /
 * `Visibility='InternalUsers'`); `toolbox_salesforce` owns that I/O. We use the Files API rather
 * than the legacy 25 MB `Attachment` object so a document can be up to ~2 GB. `toolbox_db` keeps
 * a compliance-safe index row and an access-audit log. So an upload is orchestrated across two
 * services here:
 *
 *   1. POST (multipart) `sf/account/{accountId}/files/`  -> Salesforce ContentDocument/Version ids + metadata
 *   2. POST `db/secure-customer-file/`                    -> the index row
 *   3. POST `db/secure-file-access-log/` (action=upload)  -> the audit entry
 *
 * Downloads are *authenticated* blob fetches, not public/signed URLs: the bytes come back through
 * the Salesforce token on `sf/...`, so we fetch them with our API headers and hand the browser a
 * short-lived object URL rather than ever exposing a shareable link. Every download and delete is
 * logged the same way an upload is, which is the point of storing these here at all.
 */
import { deleteItem, postItem, putItem } from './queries';
import { buildApiUrl, getApiHeaders } from './queriesHelpers';

/** Salesforce Files uploaded via multipart are capped at ~2 GB. */
export const MAX_CUSTOMER_FILE_BYTES = 2 * 1024 * 1024 * 1024;

/**
 * How a secure file is labelled. This drives the UI badge, not access control — the bytes are
 * governed by the linked Account's Salesforce sharing. Mirrors
 * `SecureCustomerFile.CLASSIFICATION_CHOICES` in toolbox_db.
 */
export type CustomerFileClassification = 'internal' | 'confidential' | 'restricted' | 'pii';

export const CLASSIFICATION_LABELS: Record<CustomerFileClassification, string> = {
  internal: 'Internal',
  confidential: 'Confidential',
  restricted: 'Restricted',
  pii: 'PII',
};

/** An index row from `db/secure-customer-file/` — metadata only, never the bytes. */
export type CustomerFile = {
  id: number;
  customer: string;
  /** Salesforce ContentDocument id — the stable handle used for fetch/delete. */
  content_document_id: string;
  /** Salesforce ContentVersion id — used to stream the raw bytes back. */
  content_version_id: string;
  file_name: string;
  content_type?: string | null;
  size_bytes?: number | null;
  classification: CustomerFileClassification;
  is_active?: boolean;
  metadata?: Record<string, any> | null;
  updated_by?: string | null;
  created?: string | null;
  last_updated?: string | null;
};

/** What Salesforce (via our SF endpoint) returns after creating the File. */
type SalesforceFileCreated = {
  content_version_id: string;
  content_document_id: string;
  success?: boolean;
  file_name: string;
  content_type: string;
  size_bytes: number;
};

const errorMessage = (payload: any, status: number, fallback: string): string => {
  const detail = payload?.error ?? payload?.detail ?? payload?.file_name;
  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  if (detail) return String(detail);
  if (typeof payload === 'string' && payload.trim()) return payload;
  return `${fallback} (${status})`;
};

/**
 * Raw fetch against a Salesforce-backed endpoint. Used for the multipart upload and the binary
 * download, both of which do not fit the JSON `queries.ts` helpers. `getApiHeaders` attaches both
 * the Toolbox and Salesforce tokens for `sf/` endpoints.
 */
async function sfFileFetch(
  method: 'GET' | 'POST' | 'DELETE',
  endpoint: string,
  options: { params?: Record<string, any>; form?: FormData } = {},
): Promise<Response> {
  const { params = {}, form } = options;
  const headers = { ...getApiHeaders(null, method, endpoint) } as Record<string, string>;
  // FormData sets its own multipart boundary, so the JSON content type must not be sent.
  if (form) delete headers['Content-Type'];

  return fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers,
    credentials: 'include',
    ...(form ? { body: form } : {}),
  });
}

/**
 * Upload a file for an account: store the bytes in Salesforce, then record the index row and an
 * upload audit entry. `accountId` is the Salesforce AccountId (which is also `Customer.id`).
 */
export async function uploadCustomerFile(
  accountId: string,
  file: File,
  options: { classification?: CustomerFileClassification } = {},
): Promise<CustomerFile> {
  if (file.size > MAX_CUSTOMER_FILE_BYTES) {
    throw new Error(
      `${file.name} is larger than the ${Math.round(MAX_CUSTOMER_FILE_BYTES / (1024 * 1024 * 1024))} GB Salesforce limit.`,
    );
  }

  const classification = options.classification ?? 'confidential';

  // 1. Store the bytes in Salesforce as a File on the Account.
  const form = new FormData();
  form.append('file', file);
  form.append('file_name', file.name);

  const uploadEndpoint = `sf/account/${accountId}/files/`;
  const sfResponse = await sfFileFetch('POST', uploadEndpoint, { form });
  const sfPayload = await sfResponse.json().catch(() => null);

  if (!sfResponse.ok || !sfPayload?.content_document_id) {
    throw new Error(errorMessage(sfPayload, sfResponse.status, 'Salesforce upload failed'));
  }

  const created = sfPayload as SalesforceFileCreated;

  // 2. Register the compliance index row in toolbox_db.
  const record: CustomerFile = await postItem('db/secure-customer-file', {
    customer: accountId,
    content_document_id: created.content_document_id,
    content_version_id: created.content_version_id,
    file_name: created.file_name || file.name,
    content_type: created.content_type || file.type || 'application/octet-stream',
    size_bytes: created.size_bytes ?? file.size,
    classification,
  });

  // 3. Audit the upload. Best-effort: a failed log must not undo a stored file.
  await logCustomerFileAccess(accountId, created.content_document_id, 'upload', record.id).catch(
    () => undefined,
  );

  return record;
}

/**
 * Trigger a browser download of a secure file. The bytes are auth-gated, so we fetch them with our
 * API headers and use a temporary object URL — there is deliberately no shareable link.
 */
export async function downloadCustomerFile(record: CustomerFile): Promise<void> {
  // Log first, so an access appears in the audit trail even if the browser save is cancelled.
  await logCustomerFileAccess(
    record.customer,
    record.content_document_id,
    'download',
    record.id,
  ).catch(() => undefined);

  const endpoint = `sf/account/${record.customer}/files/${record.content_document_id}/download/`;
  const response = await sfFileFetch('GET', endpoint, {
    params: { version_id: record.content_version_id, file_name: record.file_name },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(errorMessage(payload, response.status, 'Download failed'));
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.rel = 'noopener';
    anchor.download = record.file_name || record.content_document_id;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Delete a secure file: remove the Salesforce File, deactivate the index row (soft delete, so the
 * audit history's foreign key survives), then log the deletion.
 */
export async function deleteCustomerFile(record: CustomerFile): Promise<void> {
  const endpoint = `sf/account/${record.customer}/files/${record.content_document_id}/`;
  const response = await sfFileFetch('DELETE', endpoint);

  if (!response.ok && response.status !== 404) {
    const payload = await response.json().catch(() => null);
    throw new Error(errorMessage(payload, response.status, 'Delete failed'));
  }

  // Soft delete keeps the row (and its access_logs FK) around for compliance. Uses PUT, not
  // PATCH: the runtime's generic detail handler only serves GET/PUT/DELETE, and PUT applies just
  // the fields sent, so this updates `is_active` alone.
  await putItem('db/secure-customer-file', { id: record.id, is_active: false });

  await logCustomerFileAccess(
    record.customer,
    record.content_document_id,
    'delete',
    record.id,
  ).catch(() => undefined);
}

/** Write one access-audit row. Kept separate so callers can await or fire-and-forget it. */
export async function logCustomerFileAccess(
  accountId: string,
  documentId: string,
  action: 'upload' | 'download' | 'view' | 'delete',
  secureFileId?: number,
): Promise<void> {
  await postItem('db/secure-file-access-log', {
    customer: accountId,
    content_document_id: documentId,
    action,
    ...(secureFileId != null ? { secure_file: secureFileId } : {}),
  });
}

/** Human-readable size, for file rows. */
export function formatFileSize(sizeBytes?: number | null): string {
  const size = Number(sizeBytes || 0);
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
